import httpService from "@Utils/httpService";
import { $getFromLocal } from "@/utils";

const baseUrl = "api/Auth";

/**
 * Authentication service for the token-based auth system.
 *
 * Key features:
 * - Access tokens are short-lived (~15 minutes)
 * - Refresh tokens are stored in localStorage and sent in request body
 * - Supports device tracking for desktop/mobile clients
 */
export default {
  /**
   * Login with email and password.
   * Returns both accessToken and refreshToken in the response body.
   *
   * @param {Object} data - Login credentials
   * @param {string} data.email - User email
   * @param {string} data.password - User password
   * @param {string} [data.clientType='web'] - Client type: 'web', 'desktop', or 'mobile'
   * @param {string} [data.deviceId] - Unique device identifier (for desktop/mobile)
   * @param {string} [data.deviceName] - Human-readable device name (for desktop/mobile)
   * @returns {Promise} Response containing user, accessToken, refreshToken, and expiresIn
   */
  login(data) {
    return httpService.post({
      url: `${baseUrl}/login`,
      data: {
        ...data,
        clientType: data.clientType || 'web'
      }
    });
  },

  /**
   * Refresh the access token using the stored refresh token.
   *
   * @returns {Promise} Response containing new accessToken, refreshToken, and expiresIn
   */
  refreshToken() {
    const refreshToken = $getFromLocal('refreshToken', false, null);
    return httpService.post({
      url: `${baseUrl}/refresh`,
      data: { refreshToken }
    });
  },

  /**
   * Logout the current session.
   * Revokes the refresh token on the server.
   *
   * @returns {Promise}
   */
  logout() {
    const refreshToken = $getFromLocal('refreshToken', false, null);
    return httpService.post({
      url: `${baseUrl}/logout`,
      data: { refreshToken }
    });
  },

  /**
   * Logout from all sessions/devices.
   * Revokes all refresh tokens for the current user.
   *
   * @returns {Promise} Response containing count of revoked sessions
   */
  logoutAll() {
    return httpService.post({
      url: `${baseUrl}/logout-all`
    });
  },

  /**
   * Get all active sessions for the current user.
   *
   * @returns {Promise} Array of session objects
   */
  getSessions() {
    return httpService.get({
      url: `${baseUrl}/sessions`
    });
  },

  /**
   * Revoke a specific session by ID.
   *
   * @param {string} sessionId - The session ID to revoke
   * @returns {Promise}
   */
  revokeSession(sessionId) {
    return httpService.delete({
      url: `${baseUrl}/sessions/${sessionId}`
    });
  },

  // ============================================
  // SSO (Single Sign-On) Methods
  // ============================================

  /**
   * Check if SSO is available for an email address.
   * Used to determine login options (SSO button, password, or both).
   *
   * @param {string} email - User email address
   * @returns {Promise<CheckEmailSsoResult>} SSO availability info:
   *   - ssoAvailable: boolean - Whether SSO is configured for this email's domain
   *   - passwordLoginAllowed: boolean - Whether password login is also allowed
   *   - ssoDisplayName: string|null - Display text for SSO button
   *   - companyId: string|null - Company ID for SSO login redirect
   *   - userExists: boolean - Whether the user already exists in the system
   *   - canAutoProvision: boolean - Whether new user can be auto-provisioned via SSO
   *   - organizationName: string|null - Organization name from SSO configuration
   *   - error: string|null - Error message if SSO is not available
   */
  checkEmailSSO(email) {
    return httpService.post({
      url: 'api/sso/check-email',
      data: { email }
    });
  },

  /**
   * Initiate SSO login flow.
   * Calls the backend to get the authorization URL, then redirects to Azure AD.
   *
   * @param {string} companyId - Company ID for tenant-specific SSO config
   * @param {string} [returnUrl] - Optional URL to redirect after successful login
   * @returns {Promise<SsoInitiateResult>} SSO initiation result:
   *   - authorizationUrl: string - URL to redirect user to for authentication
   *   - state: string - State token for verification
   */
  initiateSSOLogin(companyId, returnUrl = null) {
    let url = `api/sso/login?companyId=${encodeURIComponent(companyId)}`;
    if (returnUrl) {
      url += `&returnUrl=${encodeURIComponent(returnUrl)}`;
    }
    return httpService.get({ url });
  },

  /**
   * Initiate SSO login and immediately redirect to Azure AD.
   * Convenience method that calls initiateSSOLogin and redirects.
   *
   * @param {string} companyId - Company ID for tenant-specific SSO config
   * @param {string} [returnUrl] - Optional URL to redirect after successful login
   */
  async initiateSSOLoginAndRedirect(companyId, returnUrl = null) {
    const response = await this.initiateSSOLogin(companyId, returnUrl);
    if (response && response.authorizationUrl) {
      window.location.href = response.authorizationUrl;
    }
  },

  /**
   * Get the SSO login URL without redirecting.
   * Note: This now requires an API call to get the authorization URL.
   *
   * @param {string} companyId - Company ID for tenant-specific SSO config
   * @param {string} [returnUrl] - Optional URL to redirect after successful login
   * @returns {Promise<string>} The authorization URL
   */
  async getSSOLoginUrl(companyId, returnUrl = null) {
    const response = await this.initiateSSOLogin(companyId, returnUrl);
    return response?.authorizationUrl || null;
  }
};
